
import React from 'react';
import { LogOut, ChevronDown, ArrowUp, UserCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getUserInitials } from './utils';

interface DesktopNavProps {
  navLinks: Array<{ name: string; href: string }>;
}

const DesktopNav: React.FC<DesktopNavProps> = ({ navLinks }) => {
  const { user, isGuest, signOut, convertGuestToFull } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <div className="flex items-center">
      <nav className="flex items-center space-x-1 mr-4">
        {navLinks.map((link) => (
          <a 
            key={link.name} 
            href={link.href} 
            className="navbar-link"
          >
            {link.name}
          </a>
        ))}
      </nav>
      
      {user ? (
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center space-x-2 text-white outline-none">
            <div className="flex items-center">
              <Avatar className="h-8 w-8 mr-2">
                <AvatarImage src={user.user_metadata?.avatar_url} />
                <AvatarFallback className="bg-glowingTeal/80 text-white">
                  {getUserInitials(user, isGuest)}
                </AvatarFallback>
              </Avatar>
              {isGuest && (
                <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded mr-1">
                  Guest
                </span>
              )}
              <ChevronDown className="h-4 w-4 ml-1" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => navigate('/profile')}>
              <UserCircle className="mr-2 h-4 w-4" />
              <span>My Profile</span>
            </DropdownMenuItem>
            
            {isGuest ? (
              <DropdownMenuItem onClick={convertGuestToFull}>
                <ArrowUp className="mr-2 h-4 w-4" />
                <span>Upgrade to Full Account</span>
              </DropdownMenuItem>
            ) : (
              /* Removed "My Trips" menu item since it's in the top nav */
              <></>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut}>
              <LogOut className="mr-2 h-4 w-4" />
              <span>Sign Out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <a 
          href="/auth" 
          className="text-white text-sm font-medium bg-glowingTeal/30 hover:bg-glowingTeal/50 px-3 py-1.5 rounded-md transition-colors"
        >
          Sign In
        </a>
      )}
    </div>
  );
};

export default DesktopNav;
